import React, { useState, useRef, useEffect } from 'react'
import { Calendar, Clock, Users, QrCode, X, CheckCircle, XCircle } from 'lucide-react'
import QrScanner from 'qr-scanner'
import axios from 'axios'
import TokenService from '@/services/api/tokenService'

const MovieCard = ({ movie, onClick, checkControl = false }) => {
  const [showScanner, setShowScanner] = useState(false)
  const [scanResult, setScanResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [checkStatus, setCheckStatus] = useState(null) // 'success', 'error', null
  const [responseMessage, setResponseMessage] = useState('') // Sunucudan gelen mesaj
  const videoRef = useRef(null)
  const scannerRef = useRef(null)

  const total = movie.capacity || 0
  const taken = movie.taken || 0
  const percent = total > 0 ? Math.round((taken / total) * 100) : 0

  useEffect(() => {
    if (showScanner && videoRef.current && !scanResult) {
      scannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          setScanResult(result.data)
          console.log('QR Kod:', result.data)
          // QR okununca tarayıcıyı durdur
          if (scannerRef.current) {
            scannerRef.current.stop()
          }
          handleQrResult(result.data)
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
        }
      )

      scannerRef.current.start()
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop()
        scannerRef.current.destroy()
      }
    }
  }, [showScanner, scanResult])

  const handleQrResult = async (qrData) => {
    try {
      setLoading(true)
      setCheckStatus(null)
      setResponseMessage('')

      const token = TokenService.getToken()

      const response = await axios.post(
        'https://app.yanibasimdakaratay.com/api/genclik-merkezi-sinema-salonu-check-qr',
        {
          qrCode: qrData,
          dateId: movie.date_id,
          hourId: movie.hour_id,
        },
        {
          headers: {
            Authorization: token,
          },
        }
      )

      if (response.data.success) {
        setCheckStatus('success')
        setResponseMessage(response.data.message || 'Bilet Geçerli')
        // Başarılı mesajı 2 saniye göster, sonra modalı kapat ve sayfayı yenile
        setTimeout(() => {
          setShowScanner(false)
          location.reload()
        }, 2000)
      } else {
        setCheckStatus('error')
        setResponseMessage(response.data.message || 'Bilet Geçersiz')
      }
    } catch (error) {
      console.error('QR kontrol hatası:', error)
      setCheckStatus('error')
      setResponseMessage(error.response?.data?.message || 'Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleQrClick = (e) => {
    e.stopPropagation()
    setScanResult(null)
    setCheckStatus(null)
    setResponseMessage('')
    setShowScanner(true)
  }

  const handleCloseScanner = (e) => {
    e.stopPropagation()
    setShowScanner(false)
    setScanResult(null)
    setCheckStatus(null)
    setResponseMessage('')
  }

  const handleTryAgain = (e) => {
    e.stopPropagation()
    setScanResult(null)
    setCheckStatus(null)
    setResponseMessage('')
    // Tarayıcıyı yeniden başlat
    if (scannerRef.current) {
      scannerRef.current.start()
    }
  }

  return (
    <>
      <div onClick={onClick} className='bg-white border border-[#AC5E2A] rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-xl transition-shadow'>
        <div className='p-3 relative space-y-4'>
          <h3 className='font-bold text-lg text-black-100 line-clamp-2'>Seans Seç</h3>

          <div className='space-y-2'>
            {movie.date && (
              <div className='flex items-center text-sm text-gray-600'>
                <Calendar size={16} className='mr-2 text-[#AC5E2A]' />
                <span>{movie.date}</span>
              </div>
            )}

            {movie.hour && (
              <div className='flex items-center text-sm text-gray-600'>
                <Clock size={16} className='mr-2 text-[#AC5E2A]' />
                <span>{movie.hour}</span>
              </div>
            )}
          </div>

          <div className='space-y-1 mt-4'>
            <div className='flex items-center justify-between text-xs text-gray-700'>
              <span>Doluluk</span>
              <span className='font-semibold'>{percent}%</span>
            </div>

            <div className='w-full bg-gray-200 rounded-full h-2 overflow-hidden'>
              <div className='h-full bg-[#AC5E2A] transition-all' style={{ width: `${percent}%` }}></div>
            </div>

            <div className='flex items-center text-xs text-gray-500 mt-1'>
              <Users size={14} className='mr-1 text-[#AC5E2A]' />
              <span>
                {taken} / {total} koltuk
              </span>
            </div>

            {checkControl && (
              <button onClick={handleQrClick} className='mt-3 p-2 flex gap-1 text-sm w-full justify-center items-center text-white bg-[#AC5E2A] rounded-lg hover:bg-[#8B4A1F] transition-colors'>
                <QrCode size={20} className='text-white' /> Bilet Kontrol Et
              </button>
            )}
          </div>
        </div>
      </div>

      {/* QR Scanner Modal */}
      {showScanner && (
        <div className='fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center' onClick={handleCloseScanner}>
          <div className='relative w-full max-w-md mx-4' onClick={(e) => e.stopPropagation()}>
            <button onClick={handleCloseScanner} className='absolute -top-12 right-0 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors'>
              <X size={24} className='text-black' />
            </button>

            <div className='bg-white rounded-xl overflow-hidden'>
              {!scanResult && <video ref={videoRef} className='w-full' />}

              <div className='p-6 text-center'>
                {loading && (
                  <div className='space-y-3'>
                    <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#AC5E2A] mx-auto'></div>
                    <p className='text-sm text-gray-600'>Bilet kontrol ediliyor...</p>
                  </div>
                )}

                {!loading && !checkStatus && !scanResult && <p className='text-sm text-gray-600'>QR kodu kameranın önüne tutun</p>}

                {!loading && checkStatus === 'success' && (
                  <div className='space-y-3'>
                    <CheckCircle size={48} className='text-green-500 mx-auto' />
                    <p className='text-lg font-semibold text-green-600'>✓ {responseMessage}</p>
                  </div>
                )}

                {!loading && checkStatus === 'error' && (
                  <div className='space-y-3'>
                    <XCircle size={48} className='text-red-500 mx-auto' />
                    <p className='text-lg font-semibold text-red-600'>✗ {responseMessage}</p>
                    <button onClick={handleTryAgain} className='mt-4 px-6 py-2 bg-[#AC5E2A] text-white rounded-lg hover:bg-[#8B4A1F] transition-colors'>
                      Tekrar Dene
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MovieCard
